import React, { useEffect, useState } from 'react';

import {
  Button,
  CircularProgress,
  Divider,
  LoadingOverlay,
  makeStyles,
  Menu,
  MenuItem,
  Tab,
  TabPanel,
  Tabs,
  Text,
  ThemeProvider,
} from '@knockrentals/knock-shared-web';
import { Tooltip } from '@material-ui/core';
import { KeyboardArrowDown } from '@material-ui/icons';
import { Toaster } from '@knockrentals/knock-react';
import { useAppContext } from '../../Context/AppContext';
import { useFeatureFlagContext } from '../../Context/FeatureFlagContext';
import PropertiesAPI from '../Properties/PropertiesAPI';
import ProductEnabledSettings from './NewConfiguration/ProductEnabledSettings';
import { getIsAiEmailEnabled } from './aiEmailAPI';
import {
  downloadInternalConfig,
  downloadLeasingPropertyResponses,
  downloadProductsEnabledConfig,
  downloadResidentPropertyResponses,
  downloadVirtualAgentConfig,
  downloadVirtualAgentResidentConfig,
  getRenterAISettings,
} from './VirtualAgentAPI';
import VirtualAgentConfigSettings from './NewConfiguration/VirtualAgentConfigSettings';
import VirtualAgentResidentConfigSettings from './NewConfiguration/VirtualAgentResidentConfigSettings';
import InternalConfigSettings from './NewConfiguration/InternalConfigSettings';
import VirtualAgentResponsesDownloadModal from './NewResponses/Components/VirtualAgentResponsesDownloadModal';
import { DownloadIcon } from '../../Components/Icons';
import {
  AI_AGENTS_PAGE_DESCRIPTION,
  AI_AGENTS_PAGE_NAME,
  AI_STATUS_ACTIVE,
  STATUS,
  TABS_TOOLTIP,
} from './constants';
import './_VirtualAgentPage.scss';

const useStyles = makeStyles(() => ({
  tabPanel: {
    marginTop: '10px',
  },

  secondaryText: {
    marginBottom: '24px',
  },

  divider: {
    marginTop: '0 !important',
    position: 'relative',
    top: '-1px',
  },

  subtitle: {
    marginTop: '10px',
    marginBottom: '25px',
  },

  buttons: {
    paddingTop: '15px',
    display: 'flex',
    gap: '10px',
  },
}));

const TABS = {
  CONFIG: 'config',
  RESPONSES: 'responses',
  SETUP: 'setup',
  PRODUCTS: 'products',
  VIRTUAL_AGENT: 'virtual_agent',
  VIRTUAL_AGENT_RESIDENT: 'virtual_agent_resident',
  INTERNAL_CONFIG: 'internal_config',
};

const TABS_NAMES = {
  responses: 'Responses',
  setup: 'Setup Shortcuts',
  products: 'Products and Services',
  virtual_agent: 'AI Leasing',
  virtual_agent_resident: 'AI Resident',
  internal_config: 'General Settings',
};

const DOWNLOADABLE_TABS = [
  { value: TABS.PRODUCTS, label: 'Products and Services' },
  { value: TABS.INTERNAL_CONFIG, label: 'General Settings' },
  { value: TABS.VIRTUAL_AGENT, label: 'AI Leasing' },
  { value: TABS.VIRTUAL_AGENT_RESIDENT, label: 'AI Resident' },
];

const NewVirtualAgent = () => {
  const classes = useStyles();
  const { companyId, isInternalMode: internalMode } = useAppContext();
  const { isUnifiedLeasingSettingsEnabled, isUnifiedResidentSettingsEnabled } =
    useFeatureFlagContext();
  const [currentTab, setCurrentTab] = useState(TABS.PRODUCTS);
  const [downloadingTab, setDownloadingTab] = useState('');
  const [productsEnabled, setProductsEnabled] = useState([]);
  const [fetchedPropertyData, setFetchedPropertyData] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadModalErrorMessage, setDownloadModalErrorMessage] =
    useState('');
  const [downloadMenuAnchorEl, setDownloadMenuAnchorEl] = useState(null);
  const handleTabsChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  useEffect(() => {
    const fetchPropertyData = async () => {
      try {
        const response = await PropertiesAPI.getCommunityInfo();
        const properties = response?.properties || [];

        properties.sort((a, b) =>
          a.data.location.name.localeCompare(b.data.location.name),
        );

        const propertyIds = properties.map(({ id }) => id);

        const [aiEmailEnabledList, renterAISettingsList] = await Promise.all([
          getIsAiEmailEnabled(propertyIds),
          getRenterAISettings(companyId),
        ]);

        const mergedProperties = properties.map((property) => {
          const aiEmail = aiEmailEnabledList.find(
            ({ property_id }) => property_id === property.id,
          );

          const renterAI = renterAISettingsList.find(
            ({ property_id }) => property_id === property.id,
          );

          return {
            propertyId: property.id,
            propertyName: property.data.location.name,
            emailProductEnabled: aiEmail?.ai_email_enabled ?? false,
            aiEmailEnabled: aiEmail?.ai_email_enabled ?? false,
            genAiEmailEnabled: aiEmail?.gen_ai_enabled ?? false,
            leasingEnabled: renterAI?.ai_leasing === AI_STATUS_ACTIVE,
            residentEnabled: renterAI?.ai_resident === AI_STATUS_ACTIVE,
          };
        });

        setProductsEnabled(mergedProperties);
      } catch (error) {
        Toaster.showToast(
          'Error fetching property data',
          2000,
          Toaster.ToastClasses.error,
        );
      } finally {
        setFetchedPropertyData(true);
        setIsLoading(false);
      }
    };

    if (!fetchedPropertyData) {
      setIsLoading(true);
      fetchPropertyData();
    }
  }, [companyId, fetchedPropertyData]);

  const getCSVFile = (csvResponse, filename) => {
    const blob = new Blob([csvResponse], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);

    const download_link = Object.assign(document.createElement('a'), {
      href: url,
      download: filename,
      style: 'display: none',
    });

    document.body.appendChild(download_link);
    download_link.click();
    document.body.removeChild(download_link);
    window.URL.revokeObjectURL(url);
  };

  const handleDownloadConfirmation = async (propertyIds) => {
    setIsDownloadModalOpen(false);
    setIsDownloading(true);

    const selectedProperties = productsEnabled.filter((product) =>
      propertyIds.includes(product['propertyId']),
    );

    try {
      let res;
      const properties = {};
      selectedProperties.forEach((property) => {
        properties[property.propertyId] = {
          property_name: property.propertyName,
          ai_email_enabled: property.aiEmailEnabled === STATUS.ON,
          is_gen_ai_email_enabled: property.genAiEmailEnabled,
        };
      });
      if (downloadingTab === TABS.PRODUCTS) {
        res = await downloadProductsEnabledConfig(companyId, properties);
      }

      if (downloadingTab === TABS.INTERNAL_CONFIG) {
        res = await downloadInternalConfig(companyId, properties);
      }

      if (downloadingTab === TABS.VIRTUAL_AGENT) {
        res = await downloadVirtualAgentConfig(companyId, properties);
      }
      if (downloadingTab === TABS.VIRTUAL_AGENT_RESIDENT) {
        res = await downloadVirtualAgentResidentConfig(companyId, properties);
      }
      if (downloadingTab === TABS.RESPONSES) {
        let residentCsvResponse =
          await downloadResidentPropertyResponses(selectedProperties);
        getCSVFile(residentCsvResponse, 'resident_responses.csv');

        let leasingCsvResponse =
          await downloadLeasingPropertyResponses(selectedProperties);
        getCSVFile(leasingCsvResponse, 'leasing_responses.csv');
      } else {
        getCSVFile(res, `${TABS_NAMES[downloadingTab]}.csv`);
      }
      Toaster.showToast(
        `${TABS_NAMES[downloadingTab]} downloaded successfully`,
        2000,
        Toaster.ToastClasses.success,
      );
    } catch (e) {
      Toaster.showToast(
        `An error occurred while downloading the ${TABS_NAMES[downloadingTab]}.`,
        2000,
        Toaster.ToastClasses.error,
      );
    }
    setIsDownloading(false);
  };

  const handleDownloadMenuOpen = (event) => {
    setDownloadMenuAnchorEl(event.currentTarget);
  };

  const handleDownloadMenuClose = () => {
    setDownloadMenuAnchorEl(null);
  };

  const handleDownloadTabSelect = (tabValue) => {
    setDownloadMenuAnchorEl(null);
    setDownloadingTab(tabValue);
    setIsDownloadModalOpen(true);
  };

  const isDownloadButtonVisible =
    internalMode &&
    currentTab !== TABS.SETUP;

  return (
    <ThemeProvider>
      <section className="virtual-agents">
        <div className="page-header">
          <Text variant="h6">{AI_AGENTS_PAGE_NAME}</Text>
          {isDownloadButtonVisible && (
            <React.Fragment>
              <Button
                aria-controls={downloadMenuAnchorEl ? 'download-menu' : undefined}
                aria-expanded={downloadMenuAnchorEl ? 'true' : undefined}
                aria-haspopup="true"
                endIcon={
                  isDownloading ? (
                    <CircularProgress size="20px" color="#fff" />
                  ) : (
                    <KeyboardArrowDown />
                  )
                }
                onClick={handleDownloadMenuOpen}
                size="large"
                startIcon={<DownloadIcon color="#fff" />}
                disabled={isDownloading}
              >
                Download Config (.CSV)
              </Button>

              <Menu
                anchorEl={downloadMenuAnchorEl}
                anchorOrigin={{
                  horizontal: 'left',
                  vertical: 'bottom',
                }}
                id="download-menu"
                onClose={handleDownloadMenuClose}
                open={Boolean(downloadMenuAnchorEl)}
                transformOrigin={{
                  horizontal: 'left',
                  vertical: 'top',
                }}
              >
                {DOWNLOADABLE_TABS.map((tab) => {
                  if (
                    (isUnifiedLeasingSettingsEnabled &&
                      tab.value === TABS.VIRTUAL_AGENT) ||
                    (isUnifiedResidentSettingsEnabled &&
                      tab.value === TABS.VIRTUAL_AGENT_RESIDENT)
                  ) {
                    return null;
                  }
                  return (
                    <MenuItem
                      key={tab.value}
                      onClick={() => handleDownloadTabSelect(tab.value)}
                    >
                      {tab.label}
                    </MenuItem>
                  );
                })}
              </Menu>
            </React.Fragment>
          )}
        </div>

        <Text color="textSecondary" variant="body2" className={classes.secondaryText} >{AI_AGENTS_PAGE_DESCRIPTION}</Text>

        <Tabs
          indicatorColor="primary"
          textColor="primary"
          value={currentTab}
          onChange={handleTabsChange}
        >

        <Tab
          label={
            <Tooltip title={TABS_TOOLTIP.PRODUCTS}>
              <span>
                {TABS_NAMES.products}
                <i className="ml-1 fa-regular fa-question-circle tooltip"></i>
              </span>
            </Tooltip>
          }
          value={TABS.PRODUCTS}
        />
        {internalMode && (
        <Tab
          label={
            <Tooltip title={TABS_TOOLTIP.GENERAL_SETTINGS}>
              <span>
                {TABS_NAMES.internal_config}
                <i className="ml-1 fa-regular fa-question-circle tooltip"></i>
              </span>
            </Tooltip>
          }
          value={TABS.INTERNAL_CONFIG}
        />
        )}
          {internalMode && !isUnifiedLeasingSettingsEnabled && (
            <Tab
              label={
                <Tooltip title={TABS_TOOLTIP.AI_LEASING}>
                  <span>
                    {TABS_NAMES.virtual_agent}
                    <i className="ml-1 fa-regular fa-question-circle tooltip"></i>
                  </span>
                </Tooltip>
              }
              value={TABS.VIRTUAL_AGENT}
            />
          )}
          {internalMode && !isUnifiedResidentSettingsEnabled && (
            <Tab
              label={
                <Tooltip title={TABS_TOOLTIP.AI_RESIDENT}>
                  <span>
                    {TABS_NAMES.virtual_agent_resident}
                    <i className="ml-1 fa-regular fa-question-circle tooltip"></i>
                  </span>
                </Tooltip>
              }
              value={TABS.VIRTUAL_AGENT_RESIDENT}
            />
          )}
        </Tabs>
        <Divider className={classes.divider} />

        <VirtualAgentResponsesDownloadModal
          closeDialog={() => {
            setIsDownloadModalOpen(false);
          }}
          errorMessage={downloadModalErrorMessage}
          handleConfirmation={handleDownloadConfirmation}
          isOpen={isDownloadModalOpen}
          setErrorMessage={setDownloadModalErrorMessage}
          title={`Select the properties to include in ${TABS_NAMES[downloadingTab]} export`}
        />
          <LoadingOverlay
            open={isLoading}
            style={{ position: 'absolute', opacity: '0.5' }}
          />
          <TabPanel
            className={classes.tabPanel}
            id="ProductsTabPanel"
            index={TABS.PRODUCTS}
            value={currentTab}
          >
            <ProductEnabledSettings
              companyId={companyId}
              openDownloadModal={setIsDownloadModalOpen}
              productsEnabled={productsEnabled}
              setProductsEnabled={setProductsEnabled}
            />
          </TabPanel>
          {internalMode && !isUnifiedLeasingSettingsEnabled && (
            <TabPanel
              className={classes.tabPanel}
              id="VirtualAgentConfigTabPanel"
              index={TABS.VIRTUAL_AGENT}
              value={currentTab}
            >
              <VirtualAgentConfigSettings
                companyId={companyId}
                properties={productsEnabled}
                setProductsEnabled={setProductsEnabled}
              />
            </TabPanel>
          )}

          {internalMode && !isUnifiedResidentSettingsEnabled && (
            <TabPanel
              className={classes.tabPanel}
              id="VirtualAgentResidentConfigTabPanel"
              index={TABS.VIRTUAL_AGENT_RESIDENT}
              value={currentTab}
            >
              <VirtualAgentResidentConfigSettings
                companyId={companyId}
                properties={productsEnabled}
              />
            </TabPanel>
          )}

          {internalMode && (
            <TabPanel
              className={classes.tabPanel}
              id="InternalConfigTabPanel"
              index={TABS.INTERNAL_CONFIG}
              value={currentTab}
            >
              <InternalConfigSettings
                companyId={companyId}
                properties={productsEnabled}
              />
            </TabPanel>
          )}
      </section>
    </ThemeProvider>
  );
};

export default NewVirtualAgent;
